/** Typed client over the cp3 `ROUTES` table. Errors come back as values (`ApiResult`), never thrown (api/types.ts). */
import { ROUTES, type BodyOf, type ResponseOf, type RouteName } from "@bsim/contracts";
import type { ApiResult, QueryOf, RouteCall, RouteParams } from "./types";

export interface ApiClient {
  call<N extends RouteName>(name: N, params: RouteParams<N>, body: BodyOf<N>, headers?: Readonly<Record<string, string>>, query?: QueryOf<N>): Promise<ApiResult<ResponseOf<N>>>;
  route<N extends RouteName>(name: N): RouteCall<N>;
}

const NETWORK_ERROR = { code: "INTERNAL", message: "Network error", requestId: "" } as const;

/** '/api/v1/teams/:teamId/state' + { teamId } → '/api/v1/teams/<id>/state'. */
const fillPath = (path: string, params: Readonly<Record<string, string>> | undefined): string =>
  path.replace(/:(\w+)/g, (_, key: string) => encodeURIComponent(params?.[key] ?? ""));

/** '?a=1&b=x' from the defined entries of a query object; empty when there are none. */
const queryString = (query: unknown): string => {
  const entries = Object.entries((query ?? {}) as Record<string, unknown>).filter(([, v]) => v !== undefined);
  return entries.length === 0 ? "" : `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
};

export function createApiClient(opts: { readonly baseUrl?: string; readonly fetch?: typeof fetch } = {}): ApiClient {
  const baseUrl = opts.baseUrl ?? "";
  const doFetch = opts.fetch ?? ((input, init) => fetch(input, init));

  async function call<N extends RouteName>(name: N, params: RouteParams<N>, body: BodyOf<N>, headers: Readonly<Record<string, string>> = {}, query?: QueryOf<N>): Promise<ApiResult<ResponseOf<N>>> {
    const def: { readonly method: string; readonly path: string } = ROUTES[name];
    const hasBody = body !== undefined && def.method !== "GET";
    let res: Response;
    try {
      res = await doFetch(baseUrl + fillPath(def.path, params as Readonly<Record<string, string>> | undefined) + queryString(query), {
        method: def.method,
        credentials: "same-origin",
        headers: { accept: "application/json", ...(hasBody ? { "content-type": "application/json" } : {}), ...headers },
        ...(hasBody ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      return { ok: false, status: 0, error: NETWORK_ERROR };
    }
    // A body that is not JSON (a proxy's error page, a cut-off response) is a failure value too: nothing a screen reads may throw (the page would fall to its error boundary).
    let json: unknown;
    try {
      const text = await res.text();
      json = text === "" ? undefined : JSON.parse(text);
    } catch (err) {
      console.error("api: the reply to", name, "could not be read", res.status, err); // the one place a transport failure is written down (the screen shows a plain failure)
      return { ok: false, status: res.status, error: { ...NETWORK_ERROR, message: res.ok ? "The server's reply could not be read. Try again." : res.statusText } };
    }
    if (res.ok) return { ok: true, value: json as ResponseOf<N> };
    const failure = (json ?? {}) as { error?: typeof NETWORK_ERROR; current?: unknown };
    return { ok: false, status: res.status, error: failure.error ?? { ...NETWORK_ERROR, message: res.statusText }, ...(failure.current === undefined ? {} : { current: failure.current }) };
  }

  return { call, route: (name) => (params, body) => call(name, params, body) };
}
